import React from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Alert,
} from "@mui/material";
import {
  Clear,
  FilterList,
  GridView,
  Search,
  ViewList,
} from "@mui/icons-material";
import PageTitle from "../common/PageTitle";
import { EntityStatus } from "../../types";
import type { ExplorerUrlState } from "../../hooks/useExplorerUrlState";
import type { ExplorerSortOption } from "../../utils/explorerUtils";
import {
  EXPLORER_PAGE_SIZE_CARD,
  EXPLORER_PAGE_SIZE_LIST,
} from "../../utils/explorerUtils";

const STATUS_OPTIONS = Object.values(EntityStatus);

export interface ExplorerShellProps<T> {
  pageTitle: string;
  pageDescription: string;
  loading: boolean;
  error: string | null;
  items: T[];
  totalFiltered: number;
  state: ExplorerUrlState;
  sortOptions: ExplorerSortOption[];
  filterSidebarExtra?: React.ReactNode;
  activeFilterChips?: React.ReactNode;
  hasExtraFilters?: boolean;
  emptyMessage?: string;
  renderCard: (item: T) => React.ReactNode;
  renderListRow: (item: T) => React.ReactNode;
  getItemKey: (item: T) => string;
  onClearAllExtra?: () => void;
}

function ExplorerShell<T>({
  pageTitle,
  pageDescription,
  loading,
  error,
  items,
  totalFiltered,
  state,
  sortOptions,
  filterSidebarExtra,
  activeFilterChips,
  hasExtraFilters = false,
  emptyMessage = "No items match your search or filters.",
  renderCard,
  renderListRow,
  getItemKey,
  onClearAllExtra,
}: ExplorerShellProps<T>) {
  const pageSize =
    state.layout === "list"
      ? EXPLORER_PAGE_SIZE_LIST
      : EXPLORER_PAGE_SIZE_CARD;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(state.page, totalPages);

  const handleClearAll = () => {
    state.clearAll();
    onClearAllExtra?.();
  };

  const hasFilters =
    Boolean(state.debouncedQ) ||
    state.statuses.length > 0 ||
    hasExtraFilters;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <PageTitle pageTitle={pageTitle} pageDescription={pageDescription} />

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "grey.200",
                position: { md: "sticky" },
                top: 100,
                maxHeight: { md: "calc(100vh - 120px)" },
                overflowY: "auto",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <FilterList sx={{ mr: 1, color: "primary.main", fontSize: 20 }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  Filters
                </Typography>
              </Box>

              <TextField
                fullWidth
                size="small"
                placeholder="Search..."
                value={state.q}
                onChange={(e) => state.setQ(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Status
              </Typography>
              {STATUS_OPTIONS.map((status) => (
                <FormControlLabel
                  key={status}
                  control={
                    <Checkbox
                      size="small"
                      checked={state.statuses.includes(status)}
                      onChange={() => state.toggleStatus(status)}
                    />
                  }
                  label={<Typography variant="body2">{status}</Typography>}
                />
              ))}

              {filterSidebarExtra}

              {hasFilters && (
                <Button
                  fullWidth
                  size="small"
                  startIcon={<Clear />}
                  onClick={handleClearAll}
                  sx={{ mt: 2, textTransform: "none" }}
                >
                  Clear all
                </Button>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={9}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "grey.200",
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {loading
                  ? "Loading..."
                  : `${totalFiltered} result${totalFiltered === 1 ? "" : "s"}`}
              </Typography>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Sort by</InputLabel>
                  <Select
                    label="Sort by"
                    value={state.sort}
                    onChange={(e) => state.setSort(e.target.value)}
                  >
                    {sortOptions.map((opt) => (
                      <MenuItem key={opt.id} value={opt.id}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Order</InputLabel>
                  <Select
                    label="Order"
                    value={state.sortDir}
                    onChange={(e) =>
                      state.setSortDir(e.target.value as "asc" | "desc")
                    }
                  >
                    <MenuItem value="asc">Ascending</MenuItem>
                    <MenuItem value="desc">Descending</MenuItem>
                  </Select>
                </FormControl>

                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={state.layout}
                  onChange={(_, value) => {
                    if (value) state.setLayout(value);
                  }}
                >
                  <ToggleButton value="card" aria-label="Card layout">
                    <GridView fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="list" aria-label="List layout">
                    <ViewList fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Paper>

            {state.statuses.map((s) => (
              <Chip
                key={s}
                label={s}
                size="small"
                onDelete={() => state.toggleStatus(s)}
                sx={{ mr: 0.5, mb: 1 }}
              />
            ))}
            {activeFilterChips}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
              </Box>
            ) : items.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  p: 6,
                  textAlign: "center",
                  borderRadius: 2,
                  border: "1px dashed",
                  borderColor: "grey.300",
                }}
              >
                <Typography color="text.secondary">{emptyMessage}</Typography>
                {hasFilters && (
                  <Button
                    sx={{ mt: 2 }}
                    onClick={handleClearAll}
                    startIcon={<Clear />}
                  >
                    Clear filters
                  </Button>
                )}
              </Paper>
            ) : state.layout === "card" ? (
              <Grid container spacing={2}>
                {items.map((item) => (
                  <Grid item xs={12} sm={6} md={4} key={getItemKey(item)}>
                    {renderCard(item)}
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                {items.map((item, index) => (
                  <Box
                    key={getItemKey(item)}
                    sx={{
                      borderBottom:
                        index < items.length - 1 ? "1px solid" : "none",
                      borderColor: "divider",
                    }}
                  >
                    {renderListRow(item)}
                  </Box>
                ))}
              </Paper>
            )}

            {!loading && totalFiltered > pageSize && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <Pagination
                  count={totalPages}
                  page={safePage}
                  onChange={(_, p) => state.setPage(p)}
                  color="primary"
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default ExplorerShell;
